import { useEffect, useMemo, useState } from 'react';
import { authenticatedFetch } from '../lib/auth';
import { BarChart3, Calendar, Loader2, AlertCircle } from 'lucide-react';

type PeriodMode = 'year' | 'half' | 'custom';

interface StatsResponse {
  period: { from: string; to: string };
  scope: 'all' | 'association' | 'member';
  totals: { count: number };
  top_study_plans: Array<{ study_plan_id: string | null; title: string; count: number }>;
  per_member: Array<{ member_id: string | null; member_name: string; count: number }>;
  per_association: Array<{ study_association_id: string | null; association_name: string; count: number }>;
  monthly_series: Array<{ month: string; count: number }>;
}

const monthLabelsNo: Record<string, string> = {
  '01': 'jan', '02': 'feb', '03': 'mar', '04': 'apr', '05': 'mai', '06': 'jun',
  '07': 'jul', '08': 'aug', '09': 'sep', '10': 'okt', '11': 'nov', '12': 'des',
};

function formatMonth(ym: string) {
  const [y, m] = ym.split('-');
  return `${monthLabelsNo[m]} ${y.slice(2)}`;
}

function periodBounds(mode: PeriodMode, year: number, half: 1 | 2, from: string, to: string) {
  if (mode === 'year') {
    return { from: `${year}-01-01`, to: `${year}-12-31` };
  }
  if (mode === 'half') {
    return half === 1
      ? { from: `${year}-01-01`, to: `${year}-06-30` }
      : { from: `${year}-07-01`, to: `${year}-12-31` };
  }
  return { from, to };
}

interface LineChartProps { data: Array<{ month: string; count: number }> }

function LineChart({ data }: LineChartProps) {
  if (data.length === 0) return null;

  const width = 720;
  const height = 240;
  const padL = 40, padR = 20, padT = 20, padB = 40;
  const chartW = width - padL - padR;
  const chartH = height - padT - padB;

  const maxCount = Math.max(1, ...data.map(d => d.count));
  const stepX = data.length > 1 ? chartW / (data.length - 1) : chartW;

  const points = data.map((d, i) => {
    const x = padL + i * stepX;
    const y = padT + chartH - (d.count / maxCount) * chartH;
    return { x, y, ...d };
  });

  const pathD = points.map((p, i) => (i === 0 ? `M ${p.x},${p.y}` : `L ${p.x},${p.y}`)).join(' ');
  const areaD = `${pathD} L ${points[points.length - 1].x},${padT + chartH} L ${points[0].x},${padT + chartH} Z`;

  // Y-axis ticks (0, 25%, 50%, 75%, 100% av maxCount)
  const ticks = [0, 0.25, 0.5, 0.75, 1].map(f => ({
    y: padT + chartH - f * chartH,
    label: Math.round(maxCount * f).toString(),
  }));

  // X-labels: skip enkelte hvis mange måneder
  const labelStep = data.length <= 12 ? 1 : Math.ceil(data.length / 12);

  return (
    <div className="w-full overflow-x-auto">
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full max-w-full" preserveAspectRatio="xMidYMid meet">
        {/* Grid */}
        {ticks.map((t, i) => (
          <g key={i}>
            <line x1={padL} x2={width - padR} y1={t.y} y2={t.y} stroke="#e5e7eb" strokeWidth={1} />
            <text x={padL - 8} y={t.y + 4} textAnchor="end" fontSize={11} fill="#6b7280">{t.label}</text>
          </g>
        ))}
        {/* Area */}
        <path d={areaD} fill="rgb(3 52 89 / 0.12)" />
        {/* Line */}
        <path d={pathD} fill="none" stroke="rgb(3 52 89)" strokeWidth={2} />
        {/* Points */}
        {points.map((p, i) => (
          <g key={i}>
            <circle cx={p.x} cy={p.y} r={3.5} fill="rgb(3 52 89)" />
            <title>{`${formatMonth(p.month)}: ${p.count}`}</title>
          </g>
        ))}
        {/* X-labels */}
        {points.map((p, i) => {
          if (i % labelStep !== 0 && i !== points.length - 1) return null;
          return (
            <text key={i} x={p.x} y={height - padB + 18} textAnchor="middle" fontSize={11} fill="#6b7280">
              {formatMonth(p.month)}
            </text>
          );
        })}
      </svg>
    </div>
  );
}

interface CountTableProps {
  title: string;
  rows: Array<{ label: string; count: number }>;
  emptyText?: string;
}

function CountTable({ title, rows, emptyText = 'Ingen data i valgt periode.' }: CountTableProps) {
  const max = Math.max(1, ...rows.map(r => r.count));
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-5">
      <h3 className="text-sm font-semibold text-gray-900 mb-3">{title}</h3>
      {rows.length === 0 ? (
        <p className="text-sm text-gray-500">{emptyText}</p>
      ) : (
        <div className="space-y-2">
          {rows.map((r, i) => (
            <div key={i} className="flex items-center gap-3 text-sm">
              <div className="flex-1 min-w-0 truncate text-gray-700" title={r.label}>{r.label}</div>
              <div className="w-28 h-2 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-kf-primary"
                  style={{ width: `${(r.count / max) * 100}%` }}
                />
              </div>
              <div className="w-10 text-right tabular-nums font-medium text-gray-900">{r.count}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Statistics() {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentHalf: 1 | 2 = now.getMonth() < 6 ? 1 : 2;

  const [mode, setMode] = useState<PeriodMode>('year');
  const [year, setYear] = useState<number>(currentYear);
  const [half, setHalf] = useState<1 | 2>(currentHalf);
  const [customFrom, setCustomFrom] = useState<string>(`${currentYear}-01-01`);
  const [customTo, setCustomTo] = useState<string>(now.toISOString().slice(0, 10));

  const [data, setData] = useState<StatsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { from, to } = useMemo(
    () => periodBounds(mode, year, half, customFrom, customTo),
    [mode, year, half, customFrom, customTo]
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    authenticatedFetch(`/api/statistics/certificates?from=${from}&to=${to}`)
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((json: StatsResponse) => { if (!cancelled) { setData(json); setLoading(false); } })
      .catch(err => { if (!cancelled) { setError(err.message || 'Ukjent feil'); setLoading(false); } });
    return () => { cancelled = true; };
  }, [from, to]);

  // Årsliste: 2020 til inneværende år
  const yearOptions = [];
  for (let y = currentYear; y >= 2020; y--) yearOptions.push(y);

  const scopeLabel = data?.scope === 'all' ? 'alle studieforbund'
    : data?.scope === 'association' ? 'ditt studieforbund'
    : data?.scope === 'member' ? 'din medlemsorganisasjon'
    : '';

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      <div className="flex items-center gap-3">
        <BarChart3 className="text-kf-primary" size={28} />
        <h1 className="text-2xl font-bold text-gray-900">Statistikk</h1>
      </div>

      {/* Periode-velger */}
      <div className="bg-white rounded-lg border border-gray-200 p-5 space-y-4">
        <div className="flex items-center gap-2 text-sm font-semibold text-gray-900">
          <Calendar size={16} /> Periode
        </div>
        <div className="flex flex-wrap gap-4 items-center">
          <label className="flex items-center gap-2 text-sm">
            <input type="radio" name="mode" checked={mode === 'year'} onChange={() => setMode('year')} />
            Kalenderår
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="radio" name="mode" checked={mode === 'half'} onChange={() => setMode('half')} />
            Halvår
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="radio" name="mode" checked={mode === 'custom'} onChange={() => setMode('custom')} />
            Egendefinert
          </label>
        </div>

        {mode === 'year' && (
          <div>
            <select
              value={year}
              onChange={e => setYear(Number(e.target.value))}
              className="border border-gray-300 rounded px-3 py-2 text-sm"
            >
              {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
        )}

        {mode === 'half' && (
          <div className="flex gap-3 items-center">
            <select
              value={year}
              onChange={e => setYear(Number(e.target.value))}
              className="border border-gray-300 rounded px-3 py-2 text-sm"
            >
              {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
            <select
              value={half}
              onChange={e => setHalf(Number(e.target.value) as 1 | 2)}
              className="border border-gray-300 rounded px-3 py-2 text-sm"
            >
              <option value={1}>Første halvår (jan–jun)</option>
              <option value={2}>Andre halvår (jul–des)</option>
            </select>
          </div>
        )}

        {mode === 'custom' && (
          <div className="flex flex-wrap gap-3 items-center">
            <label className="text-sm text-gray-700">Fra
              <input
                type="date"
                value={customFrom}
                onChange={e => setCustomFrom(e.target.value)}
                className="ml-2 border border-gray-300 rounded px-3 py-2 text-sm"
              />
            </label>
            <label className="text-sm text-gray-700">Til
              <input
                type="date"
                value={customTo}
                onChange={e => setCustomTo(e.target.value)}
                className="ml-2 border border-gray-300 rounded px-3 py-2 text-sm"
              />
            </label>
          </div>
        )}
      </div>

      {loading && (
        <div className="flex items-center justify-center py-16 text-gray-500">
          <Loader2 className="animate-spin mr-2" size={20} /> Laster statistikk…
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="text-red-600 shrink-0" size={20} />
          <div>
            <div className="font-semibold text-red-900">Kunne ikke hente statistikk</div>
            <div className="text-sm text-red-700">{error}</div>
          </div>
        </div>
      )}

      {data && !loading && !error && (
        <>
          {/* Nøkkeltall */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="text-sm text-gray-500 mb-1">
              Utstedte kompetanseattester i perioden {data.period.from} – {data.period.to}
              {scopeLabel && ` (${scopeLabel})`}
            </div>
            <div className="text-4xl font-bold text-kf-primary tabular-nums">{data.totals.count}</div>
            <div className="text-xs text-gray-400 mt-2">Kun aktive attester (revoked ekskludert).</div>
          </div>

          {/* Tidsserie */}
          <div className="bg-white rounded-lg border border-gray-200 p-5">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Utstedelse over tid (per måned)</h3>
            <LineChart data={data.monthly_series} />
          </div>

          {/* Grupperinger */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <CountTable
              title="Topp tema kompetanseattester"
              rows={data.top_study_plans.map(r => ({ label: r.title, count: r.count }))}
            />
            <CountTable
              title="Per medlemsorganisasjon"
              rows={data.per_member.map(r => ({ label: r.member_name, count: r.count }))}
            />
          </div>

          {data.scope === 'all' && (
            <CountTable
              title="Per studieforbund"
              rows={data.per_association.map(r => ({ label: r.association_name, count: r.count }))}
            />
          )}
        </>
      )}
    </div>
  );
}
